import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, IndianRupee, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config/api';

const WishlistPage = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      navigate('/login');
    }
  }, [user]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/wishlist/${user.id}`);
      const result = await response.json();

      if (result.success) {
        setWishlist(result.wishlist || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error fetching wishlist:', err);
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (wishlistId) => {
    try {
      setRemovingId(wishlistId);
      const response = await fetch(
        `${API_URL}/api/wishlist/${wishlistId}?user_id=${user.id}`,
        { method: 'DELETE' }
      );
      const result = await response.json();

      if (result.success) {
        setWishlist(wishlist.filter(item => item.id !== wishlistId));
      } else {
        alert(result.error || 'Failed to remove item');
      }
    } catch (err) {
      console.error('Error removing from wishlist:', err);
      alert('Failed to remove item');
    } finally {
      setRemovingId(null);
    }
  };

  const handleMoveToCart = async (item) => {
    try {
      // Add to cart - this will either add or redirect to login
      const result = await addToCart({
        id: item.product_id,
        name: item.ecom_products.name,
        price: item.ecom_products.price,
        image_url: item.ecom_products.image_url,
        category: item.ecom_products.category,
        variantId: item.variant_id,
        quantity: 1
      });

      // Handle redirect case
      if (result.redirected) {
        return; // User is being redirected to login
      }

      // Handle result
      if (result.success) {
        // Remove from wishlist
        await handleRemoveFromWishlist(item.id);
        
        // Show success feedback
        alert(`✅ ${result.message || 'Item moved to cart!'}`);
      } else if (result.error) {
        alert(`❌ ${result.error}`);
      }
    } catch (err) {
      console.error('Error moving to cart:', err);
      alert('Failed to move item to cart');
    }
  };

  const handleClearWishlist = async () => {
    if (!window.confirm('Are you sure you want to clear your entire wishlist?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/clear/${user.id}`, {
        method: 'DELETE'
      });
      const result = await response.json();

      if (result.success) {
        setWishlist([]);
      } else {
        alert(result.error || 'Failed to clear wishlist');
      }
    } catch (err) {
      console.error('Error clearing wishlist:', err);
      alert('Failed to clear wishlist');
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading wishlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-gray-900 mb-2 flex items-center gap-3">
              <Heart className="w-8 h-8 text-red-500 fill-red-500" />
              My Wishlist
            </h1>
            <p className="text-gray-600">
              {wishlist.length} {wishlist.length === 1 ? 'item' : 'items'} saved
            </p>
          </div>
          
          {wishlist.length > 0 && (
            <button
              onClick={handleClearWishlist}
              className="px-4 py-2 border-2 border-red-600 text-red-600 hover:bg-red-50 font-semibold rounded-lg transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {wishlist.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Wishlist is Empty</h2>
            <p className="text-gray-600 mb-6">
              Save items you love by clicking the heart icon on product pages
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        )}

        {/* Wishlist Grid */}
        {wishlist.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlist.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Product Image */}
                <Link
                  to={`/products/${item.product_id}`}
                  className="block relative aspect-square bg-white"
                >
                  {item.ecom_products.image_url ? (
                    <img
                      src={item.ecom_products.image_url}
                      alt={item.ecom_products.name}
                      className="w-full h-full object-cover product-img-clean"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingCart className="w-16 h-16 text-gray-300" />
                    </div>
                  )}
                  
                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemoveFromWishlist(item.id);
                    }}
                    disabled={removingId === item.id}
                    className="absolute top-3 right-3 w-10 h-10 bg-white hover:bg-red-50 rounded-full flex items-center justify-center shadow-md transition-colors disabled:opacity-50"
                  >
                    {removingId === item.id ? (
                      <div className="animate-spin w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full"></div>
                    ) : (
                      <Trash2 className="w-5 h-5 text-red-600" />
                    )}
                  </button>
                </Link>

                {/* Product Info */}
                <div className="p-4">
                  <Link
                    to={`/products/${item.product_id}`}
                    className="block mb-2"
                  >
                    <h3 className="font-bold text-gray-900 text-sm line-clamp-2 hover:text-orange-600 transition-colors">
                      {item.ecom_products.name}
                    </h3>
                  </Link>

                  {/* Category */}
                  <p className="text-xs text-gray-500 mb-2">
                    {item.ecom_products.category}
                  </p>

                  {/* Rating */}
                  {item.ecom_products.average_rating > 0 && (
                    <div className="flex items-center gap-1 mb-2">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-sm font-semibold text-gray-900">
                        {item.ecom_products.average_rating.toFixed(1)}
                      </span>
                    </div>
                  )}

                  {/* Price */}
                  <div className="flex items-center gap-1 mb-3">
                    <IndianRupee className="w-4 h-4 text-gray-900" />
                    <span className="text-lg font-black text-gray-900">
                      {parseFloat(item.ecom_products.price).toLocaleString('en-IN')}
                    </span>
                  </div>

                  {/* Move to Cart Button */}
                  <button
                    onClick={() => handleMoveToCart(item)}
                    className="w-full py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Move to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Continue Shopping */}
        {wishlist.length > 0 && (
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-block px-6 py-3 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        )}

      </div>
    </div>
  );
};

export default WishlistPage;
